import { useEffect } from 'react';
import { useSwipeable } from 'react-swipeable';

import { preventMobileHistoryNavigation } from '@/features/laneChooser/helpers';

type Direction = 'UP' | 'DOWN' | 'LEFT' | 'RIGHT';

interface UseGameControlsProps {
	gameStatus: 'playing' | 'won' | 'completed' | 'gameOver';
	waitingForFirstDirection: boolean;
	isRecovering: boolean;
	isStopped: boolean;
	directionRef: React.MutableRefObject<Direction>;
	nextDirectionRef: React.MutableRefObject<Direction>;
	onFirstDirectionChosen: () => void;
	onResume: () => void;
}

/**
 * Custom hook to handle game controls (keyboard + touch/swipe)
 *
 * Handles:
 * - Keyboard arrow key input
 * - Touch/swipe gestures for mobile/tablet using react-swipeable
 * - Direction validation (prevents 180-degree turns)
 * - First direction waiting state
 * - Resume from stopped state
 *
 * @param gameStatus - Current game status
 * @param waitingForFirstDirection - If true, waiting for player to choose initial direction
 * @param isRecovering - If true, game is paused during recovery
 * @param isStopped - If true, snake is stopped waiting for input
 * @param directionRef - Current direction reference
 * @param nextDirectionRef - Next direction reference
 * @param onFirstDirectionChosen - Callback when first direction is chosen
 * @param onResume - Callback to resume game from stopped state
 * @returns Swipeable handlers to attach to a container element
 */
export function useGameControls({
	gameStatus,
	waitingForFirstDirection,
	isRecovering,
	isStopped,
	directionRef,
	nextDirectionRef,
	onFirstDirectionChosen,
	onResume,
}: UseGameControlsProps) {
	// Minimum swipe distance to register as a direction change (in pixels)
	const SWIPE_THRESHOLD = 30;

	/**
	 * Check if a direction change is valid (no 180-degree turns)
	 */
	const isValidDirectionChange = (currentDirection: Direction, newDirection: Direction): boolean => {
		const opposites: Record<Direction, Direction> = {
			UP: 'DOWN',
			DOWN: 'UP',
			LEFT: 'RIGHT',
			RIGHT: 'LEFT',
		};
		return opposites[currentDirection] !== newDirection;
	};

	/**
	 * Apply a direction change with validation
	 */
	const applyDirectionChange = (newDirection: Direction) => {
		const currentDirection = directionRef.current;

		// Validate direction change
		if (!isValidDirectionChange(currentDirection, newDirection)) {
			return;
		}

		// Handle first direction choice
		if (waitingForFirstDirection) {
			directionRef.current = newDirection;
			nextDirectionRef.current = newDirection;
			onFirstDirectionChosen();
			return;
		}

		// Normal direction change
		nextDirectionRef.current = newDirection;

		// Resume if stopped
		if (isStopped) {
			onResume();
		}
	};

	// Keyboard controls
	useEffect(() => {
		if (gameStatus !== 'playing') return;
		if (isRecovering) return; // Don't accept input during recovery

		const handleKeyPress = (e: KeyboardEvent) => {
			let newDirection: Direction | null = null;

			switch (e.key) {
				case 'ArrowUp':
					newDirection = 'UP';
					e.preventDefault();
					break;
				case 'ArrowDown':
					newDirection = 'DOWN';
					e.preventDefault();
					break;
				case 'ArrowLeft':
					newDirection = 'LEFT';
					e.preventDefault();
					break;
				case 'ArrowRight':
					newDirection = 'RIGHT';
					e.preventDefault();
					break;
			}

			if (newDirection) {
				applyDirectionChange(newDirection);
			}
		};

		window.addEventListener('keydown', handleKeyPress);
		return () => window.removeEventListener('keydown', handleKeyPress);
	}, [gameStatus, isRecovering, isStopped, waitingForFirstDirection]);

	// Swipe controls using react-swipeable
	return useSwipeable({
		onSwiping: (data) => {
			if (data.dir === 'Left') {
				preventMobileHistoryNavigation(data.event);
			}
		},
		onSwipedUp: () => {
			if (gameStatus === 'playing' && !isRecovering) {
				applyDirectionChange('UP');
			}
		},
		onSwipedDown: () => {
			if (gameStatus === 'playing' && !isRecovering) {
				applyDirectionChange('DOWN');
			}
		},
		onSwipedLeft: () => {
			preventMobileHistoryNavigation();
			if (gameStatus === 'playing' && !isRecovering) {
				applyDirectionChange('LEFT');
			}
		},
		onSwipedRight: () => {
			if (gameStatus === 'playing' && !isRecovering) {
				applyDirectionChange('RIGHT');
			}
		},
		delta: SWIPE_THRESHOLD, // Minimum distance for swipe detection
		preventScrollOnSwipe: true, // Prevent scrolling while swiping
		trackTouch: true, // Track touch events
		trackMouse: false, // Don't track mouse events (keyboard only for desktop)
		touchEventOptions: { passive: false },
	});
}
